import { FormikProps } from "formik";
import isEqual from "lodash/isEqual";
import { FGCustomPanel, FGMaskInput, FGTextAreaInput, FGTextInput, FieldGroup, IFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import { useHistory, useLocation, useParams } from "react-router";
import * as Yup from "yup";

import {
  ChefEntrepriseSiegeEditDto,
  ChefEntrepriseSiegeEditDtoFromJSON,
  ChefsEntrepriseSiegeDoublonSearchDto,
  ReferentialApi,
  SiegeSocialApi
} from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import {
  FGEditableEmailSelectInput,
  FGEditableTelSelectInput,
  FGWalterCheckboxInput,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  FGWalterSuggestTextInput,
  NationaliteSelect,
  SmallFormGenerator,
  ViewButton
} from "../../../../../../components";
import FGCopyTextInput from "../../../../../../components/formGenerator/FGCopyTextInput";
import { useBeIdCardContext, useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTabMessage, useTheme, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";
import { belgianIdValidate } from "../../../../../../utils/belgianIdValidate";
import { phoneRegex } from "../../../../../../utils/phoneRegex";
import pick from "lodash/pick";

export interface ISiegeSocialChefEntrepriseProps {
  baseData: ChefsEntrepriseSiegeDoublonSearchDto;
}

export const SiegeSocialChefEntreprise: React.FunctionComponent<ISiegeSocialChefEntrepriseProps> = ({ baseData }) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(SiegeSocialApi);
  const refApi = useApiService(ReferentialApi);
  const formikRef = React.useRef<FormikProps<ChefEntrepriseSiegeEditDto>>();

  const { id, state, subId: idchefEntrepriseSiege } = useParams<{ id: string; state: string; subId: string }>();
  const { card } = useBeIdCardContext();
  const [cardDataDifferent, setCardDataDifferent] = React.useState(false);
  const { showDialogPromise } = useDialog();
  const { theme } = useTheme();
  const { search } = useLocation();
  const cfs = React.useMemo(() => new URLSearchParams(search).get("cfs"), [search]);
  const { sendMessage } = useTabMessage(cfs);

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors } = useCrudApi<
    ChefEntrepriseSiegeEditDto
  >(
    React.useMemo(
      () => ({
        getApiFn: () => {
          let newData = {};
          if (baseData) {
            newData = { ...baseData };
          }
          if (card) {
            newData = {
              ...newData,
              nom: card?.surname.toUpperCase(),
              prenom: card?.firstName,
              dateNaissance: card?.dateOfBirthParsed,
              registreNational: card?.nationalNumber
            };
          }
          return +idchefEntrepriseSiege <= 0
            ? ChefEntrepriseSiegeEditDtoFromJSON({
                ...newData,
                idchefEntrepriseSiege: 0,
                idsiegeSocial: +id,
                actif: true
              })
            : api.siegeSocialGetChefEntrepriseSiege({ IdchefEntrepriseSiege: +idchefEntrepriseSiege });
        },
        saveApiFn: async (d: ChefEntrepriseSiegeEditDto) => {
          return api.siegeSocialSaveChefEntrepriseSiege({ ChefEntrepriseSiegeEditDto: d });
        },
        deleteApiFn: (d: ChefEntrepriseSiegeEditDto) =>
          api.siegeSocialDeleteChefEntrepriseSiege({ IdchefEntrepriseSiege: d.idchefEntrepriseSiege }),
        onDeletedRoute: () => `${ERoutes.siegeSocial}/${+id}/chefsEntreprise/${state}`,
        onSaved: d => sendMessage(d.idchefEntreprise, true),
        onSavedRoute: d => `${ERoutes.siegeSocial}/${+id}/chefsEntreprise/${state}`
      }),
      [api, baseData, card, id, idchefEntrepriseSiege, sendMessage, state]
    )
  );

  const idpersonne = React.useMemo(() => data?.idpersonne ?? 0, [data?.idpersonne]);

  const fetchExistingFunction = React.useCallback(() => {
    return refApi.referentialGetFonctionsChefsEntreprise();
  }, [refApi]);
  const { data: existingFonctions, isFetching: efLoading } = useQuery(
    "existing-functions-chef-entreprise",
    fetchExistingFunction,
    {
      cacheTime: 0
    }
  );

  const askDataDifferent = React.useCallback(async () => {
    const result = await showDialogPromise({
      message: t(ETLCodes.ChefEntrepriseSiegeIdCardIsDifferent)
    });
    if (result === "yes") {
      setCardDataDifferent(true);
    }
  }, [showDialogPromise, t]);

  React.useEffect(() => {
    if (data && card) {
      const cardObj = ChefEntrepriseSiegeEditDtoFromJSON({
        nom: card?.surname.toUpperCase(),
        prenom: card?.firstName,
        dateNaissance: card?.dateOfBirthParsed,
        registreNational: card?.nationalNumber
      });

      const propsToPick = ["nom", "prenom", "dateNaissance", "registreNational"];
      const cardBirthdate = new Date(card.dateOfBirthParsed);
      const cardBirthdateString = `${cardBirthdate.getDate()}/${cardBirthdate.getMonth() +
        1}/${cardBirthdate.getFullYear()}`;
      const dataBirthdateString = `${data.dateNaissance.getDate()}/${data.dateNaissance.getMonth() +
        1}/${data.dateNaissance.getFullYear()}`;
      if (
        +idchefEntrepriseSiege > 0 &&
        (!isEqual(pick(cardObj, propsToPick), pick(data, propsToPick)) || cardBirthdateString !== dataBirthdateString)
      ) {
        askDataDifferent();
      }
    }
  }, [askDataDifferent, card, data, idchefEntrepriseSiege]);

  React.useEffect(() => {
    if (cardDataDifferent) {
      setCardDataDifferent(false);
      formikRef?.current?.setValues(
        ChefEntrepriseSiegeEditDtoFromJSON({
          ...data,
          nom: card?.surname.toUpperCase() ?? data.nom,
          prenom: card?.firstName ?? data.prenom,
          dateNaissance: card?.dateOfBirthParsed ?? data.dateNaissance,
          nationalite: data.nationalite ?? "BE",
          registreNational: card?.nationalNumber ?? data.registreNational
        }),
        true
      );
    }
  }, [card?.dateOfBirthParsed, card?.firstName, card?.nationalNumber, card?.surname, cardDataDifferent, data]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape(
      {
        nom: Yup.string().required(t(ETLCodes.Required)),
        prenom: Yup.string().required(t(ETLCodes.Required)),
        dateNaissance: Yup.date(),
        registreNational: Yup.string()
          .nullable()
          .test("registre-checksum", t(ETLCodes.NotValid), function(value) {
            if (!value || value === "") return true;

            var millenial = false;
            if (this.parent.dateNaissance) {
              const parsedDateNaissance = new Date(this.parent.dateNaissance);
              if (parsedDateNaissance.getFullYear() > 1999) {
                millenial = true;
              }
            }

            return belgianIdValidate(value, millenial);
          }),
        numeroIdentification: Yup.string().nullable(),
        telephone: Yup.object().shape({
          idtelephone: Yup.number()
            .nullable()
            .test("telid-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
              if (
                (!this.parent.idchefEntrepriseSiege || this.parent.idchefEntrepriseSiege <= 0) &&
                !context.from[1].value.idpersonne
              )
                return true;
              return !!value || !!context.from[1].value.gsm?.idtelephone || !!context.from[1].value.email?.idemail;
            }),
          numero: Yup.string()
            .nullable()
            .test("tel-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
              if (this.parent.idchefEntrepriseSiege > 0 || !!context.from[1].value.idpersonne) return true;
              return !!value || !!context.from[1].value.gsm?.numero || !!context.from[1].value.email?.adresseEmail;
            })
            .test("tel-valid", t(ETLCodes.ErrorPhoneRegex), function(value) {
              if (this.parent.idchefEntrepriseSiege > 0 || !value) return true;
              const numero = value as string;
              return !!numero.match(phoneRegex);
            })
        }),
        gsm: Yup.object().shape({
          idtelephone: Yup.number()
            .nullable()
            .test("gsmid-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
              if (
                (!this.parent.idchefEntrepriseSiege || this.parent.idchefEntrepriseSiege <= 0) &&
                !context.from[1].value.idpersonne
              )
                return true;
              return (
                !!value || !!context.from[1].value.telephone?.idtelephone || !!context.from[1].value.email?.idemail
              );
            }),
          numero: Yup.string()
            .nullable()
            .test("gsm-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
              if (this.parent.idchefEntrepriseSiege > 0 || !!context.from[1].value.idpersonne) return true;
              return (
                !!value || !!context.from[1].value.telephone?.numero || !!context.from[1].value.email?.adresseEmail
              );
            })
            .test("gsm-valid", t(ETLCodes.ErrorPhoneRegex), function(value) {
              if (this.parent.idchefEntrepriseSiege > 0 || !value) return true;
              const numero = value as string;
              return !!numero.match(phoneRegex);
            })
        }),
        email: Yup.object()
          .shape({
            idemail: Yup.number()
              .nullable()
              .test("emailid-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
                if (
                  (!this.parent.idchefEntrepriseSiege || this.parent.idchefEntrepriseSiege <= 0) &&
                  !context.from[1].value.idpersonne
                )
                  return true;
                return (
                  !!value || !!context.from[1].value.telephone?.idtelephone || !!context.from[1].value.gsm?.idtelephone
                );
              }),
            adresseEmail: Yup.string()
              .nullable()
              .test("email-required", t(ETLCodes.AtLeastOneContactField), function(value, context: any) {
                if (this.parent.idchefEntrepriseSiege > 0 || !!context.from[1].value.idpersonne) return true;
                return !!value || !!context.from[1].value.telephone?.numero || !!context.from[1].value.gsm?.numero;
              })
          })
          .when("idchefEntrepriseSiege", {
            is: idchefEntrepriseSiege => idchefEntrepriseSiege <= 0,
            then: Yup.object().shape({
              adresseEmail: Yup.string().email(t(ETLCodes.InvalidEmail))
            })
          }),
        sortieEntreprise: Yup.date()
      },
      [
        ["numeroIdentification", "nationalite"],
        ["numeroIdentification", "registreNational"],
        ["registreNational", "nationalite"],
        ["registreNational", "numeroIdentification"],
        ["telephone", "gsm"],
        ["gsm", "email"],
        ["telephone", "email"]
      ]
    );
  }, [t]);

  const isRegNatDateNaiss = React.useCallback((ctx: IFGContext) => {
    const value = ctx.formik.values.registreNational?.replace(".", "").replace("-", "");
    if (ctx.formik.values.dateNaissance && ctx.formik.values.nationalite === "BE") {
      const parsedDateNaissance = new Date(ctx.formik.values.dateNaissance);
      if (parsedDateNaissance && value) {
        const year = value.slice(0, 2);
        const month = value.slice(2, 4);
        const day = value.slice(4, 6);
        if (
          +year !==
          +parsedDateNaissance
            .getFullYear()
            .toString()
            .substr(2, 2)
        ) {
          return false;
        }
        if (+month !== 0 && +month !== parsedDateNaissance.getMonth() + 1) {
          return false;
        }
        if (+day !== 0 && +day !== parsedDateNaissance.getDate()) {
          return false;
        }
        return true;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }, []);

  const isRegNatGenre = React.useCallback((ctx: IFGContext) => {
    const value = ctx.formik.values.registreNational?.replace(".", "")?.replace("-", "");
    if (value && ctx.formik.values.codesexe !== "X" && ctx.formik.values.nationalite === "BE") {
      const code = +value.slice(7, 9) % 2 === 0 ? "F" : "H";
      return ctx.formik.values.codesexe === code;
    }
    return true;
  }, []);

  const isFullCreation = React.useMemo(() => +idchefEntrepriseSiege <= 0 && !data?.idpersonne, [
    data?.idpersonne,
    idchefEntrepriseSiege
  ]);
  const [sexe, sLoading] = useReferential(a => a.referentialGetSexe());

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      editMode={state === "edit"}
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(`${ERoutes.siegeSocial}/${id}/chefsEntreprise/${state}`)}
      saving={saving}
      formikInnerRef={i => (formikRef.current = i)}
      validationErrors={validationErrors}
      onDelete={deleteItem}
      deleting={deleting}
      showDeleteButton={+idchefEntrepriseSiege > 0}
      deleteButtonProps={{ text: t(ETLCodes.DelierChefEntreprise), icon: "small-cross" }}
      forceEnableSave={Object.values(baseData).some(el => !!el)}
    >
      <FieldGroup columns={2}>
        <FieldGroup>
          <FGTextInput
            name="nom"
            label={t(ETLCodes.Nom)}
            maxLength={50}
            forceCase="upper"
            rightElement={
              data?.idchefEntreprise && (
                <ViewButton
                  minimal={true}
                  onClick={() => window.open(`#${ERoutes.chefEntreprise}/${data?.idchefEntreprise}/detail/view`)}
                />
              )
            }
          />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={100} />
          <FGWalterDateMaskInput name="dateNaissance" label={t(ETLCodes.DateNaissance)} />
          <FGWalterSelectInput
            name="codesexe"
            label={t(ETLCodes.Genre)}
            items={sexe}
            loading={sLoading}
            isFilterable={false}
            isCleareable={false}
          />
          <NationaliteSelect
            name="nationalite"
            codeSexeFieldName="codesexe"
            helperText={card && `${t(ETLCodes.NationaliteSurCarte)}: ${card?.nationality}`}
          />
          <FGCustomPanel>
            {ctx => (
              <FGMaskInput
                name="registreNational"
                label={t(ETLCodes.NumeroNational)}
                cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2] }}
                helperText={ctx => (
                  <>
                    {isRegNatDateNaiss(ctx) ? null : (
                      <span style={{ color: theme.warningColor }}>{t(ETLCodes.DateDeNaissanceNeCorrespondPas)}</span>
                    )}
                    {isRegNatGenre(ctx) ? null : (
                      <span style={{ color: theme.warningColor }}>{t(ETLCodes.GenreNeCorrespondPas)}</span>
                    )}
                  </>
                )}
                requiredMark={ctx?.formik?.values?.nationalite === "BE"}
              />
            )}
          </FGCustomPanel>
          <FGTextInput
            name="numeroIdentification"
            label={t(ETLCodes.NumeroIdentification)}
            visible={ctx => !!ctx.formik?.values?.nationalite && ctx.formik?.values?.nationalite !== "BE"}
          />
          {isFullCreation ? (
            <FGCopyTextInput copyOnlyDigits={true} name="gsm.numero" label={t(ETLCodes.Gsm)} maxLength={20} />
          ) : (
            <FGEditableTelSelectInput
              name="gsm.idtelephone"
              label={t(ETLCodes.Gsm)}
              visible={!isFullCreation}
              autoSelectIfOne
              idpersonne={idpersonne}
              types={["GSM_PERSO", "GSM_PRO"]}
            />
          )}
          {isFullCreation ? (
            <FGCopyTextInput copyOnlyDigits={true} name="telephone.numero" label={t(ETLCodes.Tel)} maxLength={20} />
          ) : (
            <FGEditableTelSelectInput
              name="telephone.idtelephone"
              label={t(ETLCodes.Tel)}
              visible={!isFullCreation}
              autoSelectIfOne
              idpersonne={idpersonne}
              types={["PERSO", "PRO"]}
            />
          )}
          {isFullCreation ? (
            <FGTextInput name="email.adresseEmail" label={t(ETLCodes.General_Email)} maxLength={100} />
          ) : (
            <FGEditableEmailSelectInput
              name="email.idemail"
              label={t(ETLCodes.General_Email)}
              visible={!isFullCreation}
              autoSelectIfOne
              idpersonne={idpersonne}
            />
          )}
          <FGWalterSuggestTextInput
            name="fonction"
            label={t(ETLCodes.Fonction)}
            items={existingFonctions}
            loading={efLoading}
          />
          <FGWalterDateMaskInput name="sortieEntreprise" label={t(ETLCodes.SortieEntreprise)} />
          <FGWalterCheckboxInput name="chefEntreprisePrincipal" label={t(ETLCodes.ChefEntreprisePrincipal)} />
          <FGTextAreaInput name="remarqueSiege" label={t(ETLCodes.Remarque)} />
        </FieldGroup>
      </FieldGroup>
    </SmallFormGenerator>
  );
};
